const ZENDESK_URL = "https://help.local-esim.com";
const ZENDESK_GUIDES_FALLBACK = "/categories/24879298706578-Device-Management";
const ZENDESK_GUIDES_CATEGORY = "24879298706578";
const ZENDESK_DEVICES_SECTION = "24879353720338-Compatible-Devices";
const ESIM_APP_PROXY_SUBPATH_PREFIX = "apps";
const ESIM_APP_PROXY_SUBPATH = "proxy";

/**
 * Shopify customer id for namespacing number-porting session keys (`NpSessionKeys`).
 * String `"0"` when logged out — still passed to key builders for a stable shape.
 */
const __NP_CUSTOMER_ID = "0";

/**
 * Encrypts the given plain text with the provided key using AES-256-GCM algorithm.
 * @param {string} key - The encryption key to use.
 * @param {string} plaintext - The plain text to be encrypted.
 * @returns {string} A promise that resolves with the encrypted text.
 */
function encrypt(key, plaintext) {
  return CryptoJS.AES.encrypt(plaintext, key).toString();
}

/**
 * Decrypts the given encrypted text with the provided key using AES-256-GCM algorithm.
 * @param {string} key - The encryption key to use.
 * @param {string} encrypted - The encrypted text to be decrypted.
 * @returns {string} A promise that resolves with the decrypted plain text.
 */
function decrypt(key, encrypted) {
  return CryptoJS.AES.decrypt(encrypted, key).toString(CryptoJS.enc.Utf8);
}

/**
 * Wrapper class to convert Shopify order metadata into a time comparable order
 */
class ESimOrder {
  constructor(createdDate, esim, orderId, lineItems) {
    this.createdDate = createdDate;
    this.iccid = esim.iccid;
    this.bundle = esim.bundle;
    this.token = esim.token;
    this.ref = esim.ref;
    this.orderId = orderId;
    if (lineItems[0] != null) {
      this.productName = lineItems[0].title;
    } else {
      this.productName = "";
    }
  }
}

/**
 * Wrapper class to give convenience methods for Mobile Data
 */
class MobileData {
  constructor(data) {
    this.data = data;
  }

  bytes() {
    return this.data;
  }

  kilobytes(sizeOfAKilobyte = 1000) {
    if (this.data != window.esimCardStrings.unlimitedBundle) {
      return this.data / sizeOfAKilobyte;
    }
    return this.data;
  }

  megabytes(sizeOfAMegabyte = 1000000) {
    if (this.data != window.esimCardStrings.unlimitedBundle) {
      return this.data / sizeOfAMegabyte;
    }
    return this.data;
  }

  gigabytes(sizeOfAGigabyte = 1000000000) {
    if (this.data != window.esimCardStrings.unlimitedBundle) {
      return this.data / sizeOfAGigabyte;
    }
    return this.data;
  }
}

/**
 * enum for Possible states of a Bundle
 */
const BundleState = Object.freeze({
  InstalledWithBundle: Symbol("InstalledWithBundle"),
  InstalledWithNoBundle: Symbol("InstalledWithNoBundle"),
  InstalledWithBundleQueued: Symbol("InstalledWithBundleQueued"),
  NotInstalledWithBundle: Symbol("NotInstalledWithBundle"),
  NotInstalledWithNoBundle: Symbol("NotInstalledWithNoBundle"),
});

const INSTALLED = "Installed";

/**
 *  Check customer orders, check for duplicate ICCIDs (in event of topups),
 *  Return a list of the most recent ICCID+Token combo
 *  The checking on order dates may be redundant due to ordering of shopify orders
 *  but there is no harm in checking.
 * @param {[ESimOrder]} orders           A list of ESimOrder.
 * @return {[ESimOrder]}                 A list of unique ESimOrders, with the most recent token.
 */
function getUniqueEsimsFromCustomerOrders(orders) {
  // Map with ICCID as key and and the most recent order for it
  let mostRecentUniqueOrders = new Map();

  // compare against current value held against iccid key for mostRecentUniqueOrders
  // if the order date is newer, replace the entry in map
  orders.forEach((esim) => {
    // check if iccid already exists in order map
    if (mostRecentUniqueOrders.has(esim.iccid)) {
      var prevOrder = mostRecentUniqueOrders.get(esim.iccid);
      var prevDate = prevOrder.createdDate;

      // check if order being proccessed is newer than the currently held value
      if (esim.createdDate > prevDate) {
        mostRecentUniqueOrders.set(esim.iccid, esim);
        return;
      }
    } else {
      // new unique iccid
      mostRecentUniqueOrders.set(esim.iccid, esim);
    }
  });

  // return as an array of ESimOrder
  return [...mostRecentUniqueOrders].map(([_, value]) => value);
}

/**
 *
 * GET Bundles assigned to an eSim
 *
 * `/apps/proxy/api/get-bundles?iccid=$iccid`
 *
 * @param {ESimOrder} esim         An eSIM
 * @return {JSON}                  Response from API
 */
async function getEsimAssignmentData(esim) {
  let url = `/${ESIM_APP_PROXY_SUBPATH_PREFIX}/${ESIM_APP_PROXY_SUBPATH}/api/get-bundles?iccid=${esim.iccid}`;
  return fetch(url, {
    method: "GET",
  }).then((response) =>
    response.json().then(function (data) {
      return data;
    })
  );
}

/**
 * Esim Assignment Data returned from the eSIM Supplier API
 */
class ESimInformation {
  constructor(esim_assignment_data, esim_data) {
    this.iccid = esim_assignment_data.iccid;
    this.bundle = getBundlesByStatus(esim_assignment_data.bundles);
    this.profileStatus = esim_assignment_data.profileStatus;
    this.shopifyOrderId = esim_assignment_data.shopifyOrderId;
    this.notifications = esim_assignment_data.notifications;
    this.settings = esim_assignment_data.deviceSettings;
    this.customerRef = esim_assignment_data.customerRef;
    this.msisdnInfo = esim_assignment_data.msisdnInfo;

    this.currentBundle = esim_data.bundle;
    this.productName = esim_data.productName;
    this.createdDate = esim_data.createdDate;
    this.ref = esim_data.ref;
    this.token = esim_data.token;
    this.porting = esim_assignment_data.porting;
  }

  isPortingDay() {
    if (!this.porting) {
      return false;
    }

    const portingDate = new Date(this.porting.confirmedPortDate);
    return new Date().toDateString() >= portingDate.toDateString();
  }

  portingProgress() {
    if (!this.porting) {
      return 0;
    }
    if (
      this.porting.status === "completed" ||
      this.porting.status === "failed"
    ) {
      return 100;
    }

    if (this.porting.status !== "scheduled" && this.porting.status !== "started") {
      return 0;
    }

    const scheduledAt = new Date(this.porting.scheduledAt);
    const portingDate = new Date(this.porting.confirmedPortDate);
    const now = new Date();

    if (this.porting.status === "started" && now.getDate() === portingDate.getDate()) {
      // return the current percentage of time through the current day
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      const totalTimeToday = endOfToday.getTime() - startOfToday.getTime();
      const elapsedTimeToday = now.getTime() - startOfToday.getTime();
      return Math.max(0, Math.min(100, (elapsedTimeToday / totalTimeToday) * 100));
    }

    // Calculate progress based on elapsed time
    const totalDuration = portingDate.getTime() - scheduledAt.getTime();
    const elapsedTime = now.getTime() - scheduledAt.getTime();

    // Ensure progress is between 0 and 100
    const progressPercentage = Math.max(
      0,
      Math.min(100, (elapsedTime / totalDuration) * 100)
    );

    return progressPercentage;
  }

  isPortingCompleted() {
    if (!this.porting) {
      return false;
    }
    return this.porting.status === "completed";
  }

  expirationDate() {
    if (this.bundle.active.length == 0) {
      return new Date(0);
    } else {
      return this.bundle.active[0].assignment.endTime;
    }
  }

  remainingActiveData() {
    let activeBundles = this.bundle.active.length;
    if (activeBundles == 0) {
      return new MobileData(0);
    } else {
      if (activeBundles > 1) {
        for (const activeBundle of this.bundle.active) {
          if (activeBundle.assignment.unlimited == false) {
            return new MobileData(activeBundle.assignment.remainingQuantity);
          }
        }
      } else {
        if (this.bundle.active[0].assignment.unlimited) {
          return new MobileData(window.esimCardStrings.unlimitedBundle);
        } else {
          return new MobileData(
            this.bundle.active[0].assignment.remainingQuantity
          );
        }
      }
    }
  }

  remainingQueuedData() {
    if (this.bundle.queued.length == 0) {
      return new MobileData(0);
    } else {
      if (this.bundle.queued[0].assignment.unlimited) {
        return new MobileData(window.esimCardStrings.unlimitedBundle);
      } else {
        return new MobileData(
          this.bundle.queued[0].assignment.remainingQuantity
        );
      }
    }
  }

  fetchEsimStatsWithStatus() {
    let status = "No Plan";
    let allowances = [];
    if (this.bundle.active.length != 0) {
      allowances = this.bundle.active[0].assignment.allowances;
      status = "Installed";
      return { status, allowances };
    }

    if (this.bundle.queued.length != 0) {
      allowances = this.bundle.queued[0].assignment.allowances;
      status = "Plan not started";
      return { status, allowances };
    }

    return { status, allowances };
  }

  qrUrl() {
    return `${shopifyRootUrl}/${ESIM_APP_PROXY_SUBPATH_PREFIX}/${ESIM_APP_PROXY_SUBPATH}/app/qr/${this.settings.smdpAddress}/${this.settings.matchingId}`;
  }
}

function getBundlesByStatus(esimBundles) {
  let activeBundles = [];
  let queuedBundles = [];
  let expiredBundles = [];

  for (let b = 0; b < esimBundles.length; b++) {
    let currentBundle = JSON.parse(JSON.stringify(esimBundles[b]));
    delete currentBundle.assignments;

    for (let ba = 0; ba < esimBundles[b].assignments.length; ba++) {
      let bundleAssignment = JSON.parse(JSON.stringify(currentBundle));
      let assignment = esimBundles[b].assignments[ba];
      bundleAssignment.assignment = assignment;
      let startTime = assignment.startTime
        ? new Date(assignment.startTime)
        : null;
      let endTime = assignment.endTime ? new Date(assignment.endTime) : null;

      // if bundle is not started, it's queued.
      if (startTime == null) {
        queuedBundles.push(bundleAssignment);
        continue;
      }

      if (endTime < Date.now()) {
        expiredBundles.push(bundleAssignment);
        continue;
      }

      // check to ensure Bundle is not completed, if it is then we ignore it.
      // unless its unlimited, as remaining quantity will show remaining unthrottled data
      if (assignment.remainingQuantity > 0 || assignment.unlimited) {
        activeBundles.push(bundleAssignment);
        continue;
      }

      expiredBundles.push(bundleAssignment);
    }
  }

  return {
    active: activeBundles,
    queued: queuedBundles,
    expired: expiredBundles,
  };
}

/**
 *
 * UPDATE the `customerRef` of an eSim using the `/esims/customers` endpoint
 *
 * `https://staging-api.esim-go.com/v2.3/esims/customers/$ref`
 *
 * The `customerRef` is used as a method of allowing the end user to name their eSim.
 * @param {ESimOrder} esim         An eSim
 * @param {String} customerRef     The new name of the eSim
 * @return {JSON}                  Response from update eSIM API
 */
async function updateEsimCustomerReference(esim, customerRef) {
  const url = `${shopifyRootUrl}/${ESIM_APP_PROXY_SUBPATH_PREFIX}/${ESIM_APP_PROXY_SUBPATH}/api/update-esim?iccid=${esim.iccid}&name=${customerRef}`;

  return fetch(url, {
    method: "GET",
  }).catch((err) => console.error(err));
}

async function generatePortOutCode(iccid, portType = "PAC") {
  const url = `${shopifyRootUrl}/apps/proxy/api/port-out?iccid=${iccid}&portType=${portType}`;

  try {
    const response = await fetch(url, {
      method: "GET",
    });

    const data = await response.json();

    return data;
  } catch (error) {
    console.error(error);
    return {
      success: false,
      message: "Port Out code generation failed",
    };
  }
}

async function triggerPortIn(iccid, msisdn, authCode, requestedDate) {
  const url = `${shopifyRootUrl}/apps/proxy/api/port-in?iccid=${iccid}&msisdn=${msisdn}&authCode=${authCode}&requestedDate=${requestedDate}`;

  const response = await fetch(url, {
    method: "GET",
  });

  if (response.ok) {
    return response.json();
  } else {
    return {
      success: false,
      message: "Port In request failed",
    };
  }
}

/**
 *
 * UPDATE the Notification preferences of an eSim using the `/api/notification-prefs` endpoint
 *
 * `/apps/proxy/api/notification-prefs?iccid=$iccid&emailNotifications=$emailNotifications&smsNotifications=$smsNotifications`
 *
 * @param {ESimOrder} esim                          An eSim
 * @param {Boolean} emailNotificationsEnabled       Allow email notifications
 * @param {Boolean} smsNotificationsEnabled         Allow SMS notifications
 * @param {String} iccid                            The eSIM iccid
 * @return {JSON}                                   Response from update notification preferences API
 */
async function updateNotificationsPreferences(
  emailNotificationsEnabled,
  smsNotificationsEnabled,
  iccid
) {
  let url = `${shopifyRootUrl}/${ESIM_APP_PROXY_SUBPATH_PREFIX}/${ESIM_APP_PROXY_SUBPATH}/api/notification-prefs?iccid=${iccid}&emailNotifications=${emailNotificationsEnabled.toString()}&smsNotifications=${smsNotificationsEnabled.toString()}`;

  return fetch(url, {
    method: "GET",
  }).catch((err) => console.error(err));
}

/* Zendesk Functions */
async function getZendeskLocale(currentLocale) {
  let locale = "en-gb";

  if (currentLocale) {
    currentLocale = currentLocale.toLowerCase();

    let url = ZENDESK_URL + "/api/v2/help_center/locales";

    return fetch(url, {
      method: "GET",
    }).then((response) =>
      response
        .json()
        .then(function (res) {
          if (res.locales && res.locales.includes(currentLocale)) {
            return currentLocale;
          } else if (res.locales.length > 0) {
            for (let i = 0; i < res.locales.length; i++) {
              if (
                res.locales[i].substring(0, 2) == currentLocale.substring(0, 2)
              ) {
                return res.locales[i];
              }
            }
          }
          return locale;
        })
        .catch((err) => {
          return locale;
        })
    );
  }

  return locale;
}

async function getZendeskSections(locale, categoryId) {
  if (locale && categoryId) {
    let url =
      ZENDESK_URL +
      "/api/v2/help_center/" +
      locale +
      "/categories/" +
      categoryId +
      "/sections";

    return fetch(url, {
      method: "GET",
    }).then((response) =>
      response
        .json()
        .then(function (res) {
          return res;
        })
        .catch((err) => {
          return err;
        })
    );
  }

  return false;
}

async function getZendeskArticlesBySection(locale, sectionId) {
  if (locale && sectionId) {
    let url =
      ZENDESK_URL +
      "/api/v2/help_center/" +
      locale +
      "/sections/" +
      sectionId +
      "/articles";

    return fetch(url, {
      method: "GET",
    }).then((response) =>
      response
        .json()
        .then(function (res) {
          return res;
        })
        .catch((err) => {
          return err;
        })
    );
  }

  return false;
}

/**
 * Formats a phone number to international format with proper spacing
 * @param {string} phoneNumber - The raw phone number to format
 * @param {string} [defaultRegion='GB'] - The default region to use for parsing
 * @returns {string} The formatted phone number with + prefix and proper spacing
 */
function formatPhoneNumber(phoneNumber) {
  if (!phoneNumber) return "";

  try {
    // Parse the phone number (assuming it's in international format)
    const parsedNumber = libphonenumber.parsePhoneNumber(phoneNumber, "GB");
    // Format to international format with spaces
    return parsedNumber.formatInternational();
  } catch (error) {
    // Fallback to basic formatting if parsing fails
    return "+" + phoneNumber.replace(/(\d{2})(\d{4})(\d{6})/, "$1 $2 $3");
  }
}

async function getZendeskArticlesByLabel(locale, sectionId, label) {
  if (locale && sectionId && label) {
    let url =
      ZENDESK_URL +
      "/api/v2/help_center/articles/search.json?section=" +
      sectionId +
      "&locale=" +
      locale +
      "&label_names=" +
      label;

    return fetch(url, {
      method: "GET",
    }).then((response) =>
      response
        .json()
        .then(function (res) {
          return res;
        })
        .catch((err) => {
          return err;
        })
    );
  }

  return false;
}

/**
 * Stores number-porting context when opening the transfer intro as a modal on the same page.
 * Must mirror what account pages do so Alpine `numberPortingFlow` sees one consistent contract:
 * - **Handoff** — marks “user chose this eSIM from outside the wizard” (skip select when combined with UI flags).
 * - **Flow** — same payload for schedule / refresh; see `assets/np-session-keys.js`.
 *
 * Falls back to raw `sessionStorage` keys if `NpSessionKeys` has not loaded yet (script order).
 *
 * @param {string} customerId - Stringified Shopify customer id.
 * @param {object} esimPayload - At minimum `{ iccid, … }` as built elsewhere in this file.
 */
function persistNumberPortingModalContext(customerId, esimPayload) {
  var cid = String(customerId);

  try {
    if (typeof NpSessionKeys !== "undefined") {
      NpSessionKeys.setAccountHandoff(cid, esimPayload);
      NpSessionKeys.setFlowSelection(cid, esimPayload);
      return;
    }

    sessionStorage.setItem(
      "np_account_handoff_" + cid,
      JSON.stringify(esimPayload)
    );
    sessionStorage.setItem(
      "np_flow_selection_" + cid,
      JSON.stringify(esimPayload)
    );
  } catch (e) {
    console.error("Failed to persist number porting modal context:", e);
  }
}

/**
 * Shows the number-porting section in a full-screen overlay and optionally pre-fills the
 * selected eSIM for the modal path (handoff + flow in sessionStorage, then `number-porting-modal-open`).
 *
 * @param {object} [currentEsimData] - eSIM row to select; falls back to `window.currentEsimData` or first `__npEsimOrders` entry.
 */
function showNumberTransferIntroModal(currentEsimData) {
  const content = document.querySelector(".number-porting-intro");
  let modal = document.querySelector(".number-porting-intro-modal");
  const transferPacCardActions = document.querySelector(
    ".transfer-pac-card-actions"
  );

  // If no eSIM data provided, try to get it from the current context
  if (!currentEsimData) {
    // Try to find the current eSIM from the page context
    // This might be available in a global variable or from the current eSIM card
    if (window.currentEsimData) {
      currentEsimData = window.currentEsimData;
    } else if (window.__npEsimOrders && window.__npEsimOrders.length > 0) {
      // Use the first available eSIM order
      const firstOrder = window.__npEsimOrders[0];
      currentEsimData = {
        iccid: firstOrder.iccid,
        msisdn: "", // This would need to be fetched
        name: firstOrder.productName || firstOrder.iccid,
      };
    }
  }

  if (!modal && content) {
    // Create modal and content containers
    modal = document.createElement("div");
    modal.style.display = "flex";
    modal.classList.add("number-porting-intro-modal");

    content.classList.add("number-porting-intro-inner");
    content.style.display = "block";

    // Hide final actions after PAC code is generated
    if (transferPacCardActions) {
      transferPacCardActions.style.display = "none";
    }

    modal.appendChild(content);
    document.body.appendChild(modal);

    // Set up modal close functionality
    modal.onclick = function (target) {
      if (target.target.classList.contains("number-porting-intro-modal")) {
        modal.style.display = "none";
        content.style.display = "none";

        document.dispatchEvent(new CustomEvent("number-porting-modal-close"));
      }
    };
  } else if (modal && content) {
    content.style.display = "block";
    modal.style.display = "flex";
  }

  if (currentEsimData && content) {
    var customerId =
      typeof __NP_CUSTOMER_ID !== "undefined" ? String(__NP_CUSTOMER_ID) : "0";
    persistNumberPortingModalContext(customerId, currentEsimData);

    document.dispatchEvent(
      new CustomEvent("number-porting-modal-open", {
        detail: {
          esimData: currentEsimData,
          modalElement: modal,
        },
      })
    );
  }
}
/**
 * Different providers us slightly different units for their data allowances. Without
 * alterations this results in weird looking values in the UI. This function returns
 * the number of bytes in the standard unit for the provider so that ratios can be
 * calculated correctly and the UI looks correct for all providers.
 * @param {string} provider The provider of the eSIM
 * @param {string} unit The unit type to get for this particular provider
 * @return {number} The number of bytes in this particular unit
 */
function getStandardBytesForProviderUnit(provider, unit) {
  switch (provider) {
    case "bondio":
      switch (unit) {
        case "GB":
          return 1073741824;
        case "MB":
          return 1048576;
        case "KB":
          return 1024;
      }
      throw new Error(`Invalid unit for provider ${provider}: ${unit}`);
    case "esimgo":
    default:
      switch (unit) {
        case "GB":
          return 1000000000;
        case "MB":
          return 1000000;
        case "KB":
          return 1000;
      }
      throw new Error(`Invalid unit for provider ${provider}: ${unit}`);
  }
}

/**
 * This function takes an ESimInformation object and returns the provider of the eSIM
 * based on information stored in the bundle.
 * @param {ESimInformation} esim_info - The eSIM information to extract the provider from
 */
function getProviderFromESimInformation(esim_info) {
  if (esim_info.bundle?.active?.length > 0) {
    return esim_info.bundle.active[0].provider;
  }

  if (esim_info.bundle?.queued?.length > 0) {
    return esim_info.bundle.queued[0].provider;
  }

  if (esim_info.bundle?.expired?.length > 0) {
    return esim_info.bundle.expired[0].provider;
  }

  return null;
}

/**
 * This function takes an ESimOrder object and returns the provider of the eSIM
 * based on information stored in the bundle.
 * @param {ESimOrder} esim_order - The eSIM order to extract the provider from
 */
function getProviderFromESimOrder(esim_order) {
  if (esim_order.bundle?.active?.length > 0) {
    return esim_order.bundle.active[0].provider;
  }

  return null;
}

/**
 * Returns bytes-per-GB for a provider, using the theme's standard conversion.
 * Use this for data amount display so bondio (1024³) and esimgo (1000³) show correctly.
 * @param {string} [provider] - Provider ID from bundle (e.g. "bondio", "esimgo-domestic-uk")
 * @returns {number} Bytes in 1 GB for that provider
 */
function getBytesPerGbForProvider(provider) {
  if (typeof getStandardBytesForProviderUnit !== 'function') {
    return 1000000000;
  }
  return getStandardBytesForProviderUnit(provider || 'esimgo', 'GB');
}
